'use client';

import * as React from 'react';
import { useState, useEffect, useCallback, useRef } from 'react';
import Image from 'next/image';
import { ArrowLeft, ArrowRight, Trophy, Drop, Lightning } from '@phosphor-icons/react';
import { cn } from '@/lib/utils';

interface Slide {
  id: string;
  icon: React.ReactNode | string;
  title: string;
  subtitle: string;
}

const getLevelImage = (level: number): string => {
  if (level >= 1 && level <= 9) return '/gifs/1lvl.gif';
  if (level >= 10 && level <= 19) return '/gifs/10lvl.gif';
  if (level >= 20) return '/gifs/20lvl.gif';
  return '/gifs/1lvl.gif';
};

interface AnimatedBlocksSliderProps {
  className?: string;
  isAuthenticated?: boolean;
  currentLevel?: number;
  currentXP?: number;
  onLogin?: () => void;
  onLevelPress?: () => void;
}

export function AnimatedBlocksSlider({
  className,
  isAuthenticated = false,
  currentLevel = 1,
  currentXP = 0,
}: AnimatedBlocksSliderProps) {
  const [mounted, setMounted] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [slideDirection, setSlideDirection] = useState<'next' | 'prev'>('next');
  const autoSlideTimer = useRef<NodeJS.Timeout | null>(null);
  const lastInteractionTime = useRef(0);

  const slides: Slide[] = [
    {
      id: 'levels',
      icon: isAuthenticated ? getLevelImage(currentLevel) : <Trophy weight="fill" className="h-5 w-5 text-[var(--text-on-primary)]" />,
      title: isAuthenticated ? `Уровень ${currentLevel}` : 'Выигрывай призы',
      subtitle: isAuthenticated ? `${currentXP} XP накоплено` : 'Войди и участвуй!',
    },
    {
      id: 'sauces',
      icon: <Drop weight="fill" className="h-5 w-5 text-[var(--text-on-primary)]" />,
      title: 'Соусы отдельно',
      subtitle: 'Васаби, соевый и имбирь',
    },
  ];

  const startAutoSlide = useCallback(() => {
    if (autoSlideTimer.current) clearInterval(autoSlideTimer.current);
    autoSlideTimer.current = setInterval(() => {
      const timeSinceInteraction = Date.now() - lastInteractionTime.current;
      if (!isPaused && timeSinceInteraction > 3000) {
        setSlideDirection('next');
        setCurrentSlide((prev) => (prev + 1) % slides.length);
      }
    }, 6000);
  }, [isPaused, slides.length]);

  useEffect(() => {
    lastInteractionTime.current = Date.now();
    setMounted(true);
  }, []);

  useEffect(() => {
    startAutoSlide();
    return () => { if (autoSlideTimer.current) clearInterval(autoSlideTimer.current); };
  }, [startAutoSlide]);

  const nextSlide = useCallback(() => {
    lastInteractionTime.current = Date.now();
    setSlideDirection('next');
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  }, [slides.length]);

  const prevSlide = useCallback(() => {
    lastInteractionTime.current = Date.now();
    setSlideDirection('prev');
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  }, [slides.length]);

  const goToSlide = useCallback((index: number) => {
    lastInteractionTime.current = Date.now();
    setSlideDirection(index > currentSlide ? 'next' : 'prev');
    setCurrentSlide(index);
  }, [currentSlide]);

  const currentSlideData = slides[currentSlide];

  // SSR placeholder to avoid hydration mismatch
  if (!mounted) {
    return (
      <div className={cn(className)}>
        <div
          className="relative h-[72px] rounded-[var(--radius-2xl)] overflow-hidden"
          style={{ background: 'linear-gradient(to bottom right, #A78BFA, #7B42FF)' }}
        />
      </div>
    );
  }

  return (
    <div className={cn(className)}>
      {/* Основной контейнер */}
      <div
        className="relative h-[72px] rounded-[var(--radius-2xl)] overflow-hidden cursor-pointer"
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
        onClick={nextSlide}
      >
        {/* Градиентный фон — inline style для совместимости со старыми браузерами */}
        <div
          className="absolute inset-0 rounded-[var(--radius-2xl)]"
          style={{ background: 'linear-gradient(to bottom right, #A78BFA, #7B42FF)' }}
        />

        {/* Контент слайда */}
        <div className="relative h-full px-3 sm:px-5 flex items-center justify-between">
          <div
            key={currentSlide}
            className={cn(
              'flex items-center flex-1',
              'animate-slide-fade-in'
            )}
          >
            {/* Иконка */}
            <div className="w-11 h-11 rounded-full flex items-center justify-center overflow-hidden flex-shrink-0 bg-white/15">
              {typeof currentSlideData.icon === 'string' ? (
                <Image src={currentSlideData.icon} alt="" width={28} height={28} className="object-cover" />
              ) : (
                currentSlideData.icon
              )}
            </div>

            {/* Текст */}
            <div className="ml-4 flex-1 min-w-0">
              <p className="text-[var(--text-on-primary)] font-bold text-[15px] leading-[18px] mb-0.5 truncate">
                {currentSlideData.title}
              </p>
              <p className="text-[var(--text-on-primary)]/80 text-[13px] leading-[16px] truncate">
                {currentSlideData.subtitle}
              </p>
            </div>
          </div>

          {/* Кнопки навигации */}
          <div className="flex items-center gap-1 ml-2 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={prevSlide}
              className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-white/20 flex items-center justify-center text-[var(--text-on-primary)] hover:bg-white/30 active:scale-95 transition-all"
            >
              <ArrowLeft weight="bold" className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
            </button>
            <button
              onClick={nextSlide}
              className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-white/20 flex items-center justify-center text-[var(--text-on-primary)] hover:bg-white/30 active:scale-95 transition-all"
            >
              <ArrowRight weight="bold" className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
            </button>
          </div>
        </div>

        {/* Индикаторы */}
        <div className="absolute bottom-1 left-0 right-0 flex items-center justify-center gap-1.5">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={cn(
                'h-[2px] rounded-full transition-all duration-300',
                index === currentSlide ? 'w-5 bg-[var(--primary)]' : 'w-1 bg-[var(--primary)]/30'
              )}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
